import { SORT_ICON_ASC, SORT_ICON_DESC } from '@appViews/shared/sortIcon';
import useClientFilterOptions from '@boHooks/useClientFilterOptions';
import useCollectionOptions from '@boHooks/useCollectionOptions';
import useCollections from '@boHooks/useCollections';
import CollectionService from '@boServices/collectionService';
import { faEraser, faPlus } from '@fortawesome/pro-regular-svg-icons';
import ActiveFiltersBar from '@sharedComponents/ActiveFiltersBar';
import Button from '@sharedComponents/Button';
import FlexBox from '@sharedComponents/FlexBox';
import MultiSelectFilter from '@sharedComponents/MultiSelectFilter';
import Pagination from '@sharedComponents/Pagination';
import Panel from '@sharedComponents/Panel';
import SearchBar from '@sharedComponents/SearchBar';
import Sidebar from '@sharedComponents/Sidebar';
import Table from '@sharedComponents/Table';
import { showToast } from '@sharedComponents/Toast';
import ToggleSwitch from '@sharedComponents/ToggleSwitch';
import Typography from '@sharedComponents/Typography';
import { displayText, parseI18n } from '@src/i18n';
import { triggerBlobDownload } from '@utils/download';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { clientFilterOptions, nextClientSelection, STANDARD_OPTION } from './clientFilterRules';
import CollectionCreatePanel from './CollectionCreatePanel';
import { getCollectionColumns } from './CollectionRow';
import CollectionsPreview from './CollectionsPreview';

const collectionService = new CollectionService();

const PAGE_SIZE = 20;
// Table renders a (possibly empty) sort button on every header; only these columns are sortable server-side.
const SORTABLE_COLUMNS = ['name', 'updatedAt'];

/** Tag labels are stored as a JSON i18n text; the raw label is kept when it holds no text. */
const toFilterTag = (tag) => ({ id: tag.id, label: displayText(parseI18n(tag.valeur)) || (tag.label ?? '') });

const CollectionList = () => {
    const { t } = useTranslation('bo');
    const navigate = useNavigate();
    const searchBarRef = useRef();

    const [search, setSearch] = useState('');
    const [exporting, setExporting] = useState(false);
    const [algoliaPreview, setAlgoliaPreview] = useState(false);
    const [selectedDomainIds, setSelectedDomainIds] = useState([]);
    const [selectedClientIds, setSelectedClientIds] = useState([]);
    const [page, setPage] = useState(1);
    const [sortBy, setSortBy] = useState('-updatedAt');
    const [createOpen, setCreateOpen] = useState(false);

    const { collections, total, loading } = useCollections({
        search,
        page,
        perPage: PAGE_SIZE,
        domainTagIds: selectedDomainIds,
        clientIds: selectedClientIds.filter((id) => id !== STANDARD_OPTION),
        standard: selectedClientIds.includes(STANDARD_OPTION),
        sortBy,
    });
    const { tagsOptions } = useCollectionOptions();
    const { clients } = useClientFilterOptions();

    const totalPages = Math.ceil(total / PAGE_SIZE) || 1;

    const domainOptions = useMemo(
        () =>
            (tagsOptions.domain ?? [])
                .map(toFilterTag)
                .sort((a, b) => a.label.localeCompare(b.label, 'fr'))
                .map((tag) => ({ value: tag.id, label: tag.label })),
        [tagsOptions.domain],
    );

    const clientOptions = useMemo(
        () => [{ value: STANDARD_OPTION, label: t('Standard'), depth: 0 }, ...clientFilterOptions(clients)],
        [clients, t],
    );

    const sortIcon = sortBy.startsWith('-') ? SORT_ICON_DESC : SORT_ICON_ASC;

    const handleSort = (column) => {
        if (!SORTABLE_COLUMNS.includes(column)) return;
        setSortBy((old) => {
            const oldIdDesc = old.startsWith('-');
            if (old.includes(column)) {
                return oldIdDesc ? column : `-${column}`;
            } else {
                return oldIdDesc ? `-${column}` : column;
            }
        });
        setPage(1);
    };

    const hasActiveFilters = search.trim().length > 0 || selectedDomainIds.length > 0 || selectedClientIds.length > 0;
    const noResults = !loading && collections.length === 0;

    const handleCreate = async (payload) => {
        try {
            const created = await collectionService.createCollection(payload);
            showToast({ type: 'success', text: t('The collection has been created.') });
            navigate(`/collections/${created.id}`);
        } catch {
            showToast({ type: 'error', text: t('An error occurred while saving. Please try again.') });
        }
    };

    const handleExport = useCallback(async () => {
        setExporting(true);
        try {
            const blob = await collectionService.exportSql();
            triggerBlobDownload(blob, `catalogue-${new Date().toISOString().slice(0, 10)}.sql`);
        } catch {
            showToast({ type: 'error', title: t('Error'), text: t('An error occurred while exporting the catalogue.') });
        } finally {
            setExporting(false);
        }
    }, [t]);

    // onTextChange rather than the options flow: it ignores the react-select blur,
    // which used to clear the input without resetting the query, and it also reports
    // the empty string when the field is cleared.
    const handleSearch = useCallback((text) => {
        setSearch(text);
        setPage(1);
    }, []);

    const handleDomainChange = (ids) => {
        setSelectedDomainIds(ids);
        setPage(1);
    };

    const handleClientChange = (ids) => {
        setSelectedClientIds(nextClientSelection(clients, selectedClientIds, ids));
        setPage(1);
    };

    const clearSearch = () => {
        // SearchBar is uncontrolled: clearing the query does not empty the visible input.
        searchBarRef.current?.setInputValue('');
        setSearch('');
    };

    const handleReset = () => {
        clearSearch();
        setSelectedDomainIds([]);
        setSelectedClientIds([]);
        setPage(1);
    };

    // Chips of the active filters, in the order of the filters, each one removing its own value; a client chip as
    // unchecking the client in the list does.
    const activeFilters = [
        ...clientOptions
            .filter((option) => selectedClientIds.includes(option.value))
            .map((option) => ({
                key: `client-${option.value}`,
                label: option.label,
                remove: () =>
                    setSelectedClientIds(
                        nextClientSelection(
                            clients,
                            selectedClientIds,
                            selectedClientIds.filter((id) => id !== option.value),
                        ),
                    ),
            })),
        ...domainOptions
            .filter((option) => selectedDomainIds.includes(option.value))
            .map((option) => ({
                key: `domain-${option.value}`,
                label: option.label,
                remove: () => setSelectedDomainIds((ids) => ids.filter((id) => id !== option.value)),
            })),
        ...(search.trim() ? [{ key: 'search', label: `"${search.trim()}"`, remove: clearSearch }] : []),
    ];

    const handleRemoveFilter = (key) => {
        activeFilters.find((filter) => filter.key === key)?.remove();
        setPage(1);
    };

    return (
        <FlexBox flexDirection="column" gap="l" additionnalStyles={{ padding: 'var(--space-l) var(--space-xl)' }}>
            <FlexBox justifyContent="space-between" alignItems="center">
                <Typography fontSize="xl" fontWeight="bold" color="branded-primary" seo={1}>
                    {t('Collections')}
                </Typography>
                <FlexBox gap="s" alignItems="center">
                    <ToggleSwitch checked={algoliaPreview} onChange={(event) => setAlgoliaPreview(event.target.checked)} size="s" />
                    <Typography fontSize="s">{t('Algolia preview')}</Typography>
                    <Button label={t('Export the catalogue')} outline isLoading={exporting} action={handleExport} />
                    <Button icon={faPlus} label={t('New collection')} action={() => setCreateOpen(true)} />
                </FlexBox>
            </FlexBox>

            {algoliaPreview ? (
                <CollectionsPreview />
            ) : (
                <Panel>
                    <FlexBox justifyContent="flex-end" alignItems="center" gap="s" additionnalStyles={{ padding: 'var(--space-m)' }}>
                        <MultiSelectFilter
                            label={t('Client')}
                            options={clientOptions}
                            value={selectedClientIds}
                            onChange={handleClientChange}
                            showSearchBar
                            searchBarPlaceholder={t('Search a client…')}
                        />
                        <MultiSelectFilter
                            label={t('Subdomain')}
                            options={domainOptions}
                            value={selectedDomainIds}
                            onChange={handleDomainChange}
                            showSearchBar
                            searchBarPlaceholder={t('Search a domain…')}
                            selectAllLabel={t('All subdomains')}
                        />
                        <FlexBox additionnalStyles={{ width: '320px' }}>
                            <SearchBar
                                ref={searchBarRef}
                                hideMenu
                                compact
                                minChars={0}
                                debounceMs={300}
                                controlShouldRenderValue={false}
                                search={async () => []}
                                onTextChange={handleSearch}
                                onClear={() => handleSearch('')}
                                placeholder={t('Search a collection…')}
                            />
                        </FlexBox>
                    </FlexBox>

                    <ActiveFiltersBar color="lavender" filters={activeFilters} onRemove={handleRemoveFilter} onReset={handleReset} />

                    {noResults ? (
                        <FlexBox flexDirection="column" gap="m" alignItems="center" additionnalStyles={{ padding: 'var(--space-xl)' }}>
                            <Typography color="neutral-secondary">{t('No collections found.')}</Typography>
                            {hasActiveFilters && <Button ghost icon={faEraser} label={t('Reset filters')} action={handleReset} />}
                        </FlexBox>
                    ) : (
                        <Table
                            columns={getCollectionColumns(t, {
                                onOpen: (collection) => navigate(`/collections/${collection.id}`),
                                sortBy,
                                sortIcon,
                            })}
                            items={collections}
                            loading={loading}
                            borderless
                            getRowKey={(collection) => collection.id}
                            onSort={handleSort}
                        />
                    )}

                    {!noResults && (
                        <FlexBox
                            justifyContent="space-between"
                            alignItems="center"
                            additionnalStyles={{
                                borderTop: '1px solid var(--border-color-neutral-tertiary)',
                                padding: 'var(--space-xs) var(--space-m) var(--space-s)',
                            }}
                        >
                            <Typography fontSize="xs" color="neutral-secondary">
                                {total} {t('results')}
                            </Typography>
                            <Pagination totalPages={totalPages} selectedPage={page} onChange={setPage} />
                        </FlexBox>
                    )}
                </Panel>
            )}

            <Sidebar isOpened={createOpen} onClose={() => setCreateOpen(false)} title={t('New collection')}>
                {createOpen && <CollectionCreatePanel onCreate={handleCreate} />}
            </Sidebar>
        </FlexBox>
    );
};

export default CollectionList;
